import type { Preferences, Configuration } from '@repo/api-gw-sdk';
import {
  UpdateAccountUIPreferencesApi,
  GetAccountUIPreferencesApi,
  GetViewerUIPreferencesApi,
  UpdateViewerUIPreferencesApi,
} from '@repo/api-gw-sdk';
import { mutate } from 'swr';

import type { HttpClient } from './httpClient';
import type { PreferencesKey, PreferencesMap } from './preferencesKey';

interface ClientPreferences<K extends PreferencesKey> extends Preferences {
  value?: PreferencesMap[K];
}

const baseUrl = '/preferences';

export const preferences = (httpClient: HttpClient, config: Configuration) => {
  const getAccountUIPreferencesApi = new GetAccountUIPreferencesApi(config);
  const updateAccountUIPreferencesApi = new UpdateAccountUIPreferencesApi(
    config
  );
  const getViewerUIPreferencesApi = new GetViewerUIPreferencesApi(config);
  const updateViewerUIPreferencesApi = new UpdateViewerUIPreferencesApi(config);

  return {
    useUserPref: <K extends keyof PreferencesMap>(key: K | undefined) =>
      httpClient.executeWithConfig(
        (
          ...args: Parameters<
            typeof getViewerUIPreferencesApi.getViewerUIPreferences
          >
        ) =>
          getViewerUIPreferencesApi
            .getViewerUIPreferences(...args)
            .catch((x) => {
              if (x.code !== 404) {
                throw x;
              }
            }) as Promise<ClientPreferences<K>>,

        key ? baseUrl : null,
        'get-user-pref',
        {
          dedupingInterval: 10 * 60 * 1000, // 10 minutes
        },
        key as string
      ),
    getUserPrefAsync: <K extends keyof PreferencesMap>(key: K) =>
      getViewerUIPreferencesApi.getViewerUIPreferences(key).catch((x) => {
        if (x.code !== 404) {
          throw x;
        }
      }) as Promise<ClientPreferences<K>>,
    useAccountPref: <K extends keyof PreferencesMap>(key: K) =>
      httpClient.executeWithConfig(
        (
          ...args: Parameters<
            typeof getAccountUIPreferencesApi.getAccountUIPreferences
          >
        ) =>
          getAccountUIPreferencesApi
            .getAccountUIPreferences(...args)
            .catch((x) => {
              if (x.code !== 404) {
                throw x;
              }
            }) as Promise<ClientPreferences<K>>,

        key ? baseUrl : null,
        'get-account-pref',
        {
          dedupingInterval: 10 * 60 * 1000, // 10 minutes
        },
        key
      ),
    updateAccountPref: <K extends PreferencesKey>(
      key: K,
      value: PreferencesMap[K]
    ) =>
      updateAccountUIPreferencesApi
        .updateAccountUIPreferences(key, value)
        .then(() => mutate([baseUrl, 'get-account-pref', key])),
    updateUserPref: <K extends PreferencesKey>(
      key: K,
      value: PreferencesMap[K],
      disableRefresh?: boolean
    ) =>
      updateViewerUIPreferencesApi.updateViewerUIPreferences(key, value).then(
        // Without a refetch, still populate the cache with the written value —
        // otherwise later mounts within the deduping window read the pre-write value.
        () =>
          disableRefresh
            ? mutate<ClientPreferences<K>>(
                [baseUrl, 'get-user-pref', key],
                { key, value },
                { revalidate: false }
              )
            : mutate([baseUrl, 'get-user-pref', key])
      ),
  };
};
